import { Box, Menu, MenuItem, SxProps } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'hooks';
import {
  GRAY,
  GRAY_LIGHT_SECONDARY,
  GRAY_LIGHT_TERTIARY,
  TEXT
} from 'constants/colors';
import FontAwesomeSvgIcon from 'components/shared/FontAwesomeSvgIcon';
import {
  faAngleDown,
  faMagnifyingGlassPlus
} from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setUserPreference } from 'redux/actionCreators';

export enum ZoomLevel {
  COMPACT,
  STANDARD,
  DETAILED
}

type Props = {
  disabled?: boolean;
  disableZoomLevels?: ZoomLevel[] | null;
  minimized?: boolean;
  onSelect?: (value: ZoomLevel) => void;
  sx?: SxProps;
};

const ZoomLevelMenu = (props: Props) => {
  const { disabled, disableZoomLevels = [], onSelect, minimized, sx } = props;

  const dispatch = useDispatch();
  const zoomLevelPreference = useSelector(
    (state) => state.preferences.defaultZoomLevel
  );
  // Certain zoom levels can be disabled, in those cases we use the first available zoom level
  const defaultZoomLevel =
    zoomLevelPreference && !disableZoomLevels?.includes(zoomLevelPreference)
      ? zoomLevelPreference
      : Object.keys(ZoomLevel).find(
          (v) => !disableZoomLevels?.includes(parseInt(v) as ZoomLevel)
        );

  const { t } = useTranslation('common', {
    keyPrefix: 'activityPlanning.zoomLevel'
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = !!anchorEl;
  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOnSelect = (value: ZoomLevel) => {
    handleOnClose();
    onSelect && onSelect(value);
    dispatch(
      setUserPreference({
        key: 'defaultZoomLevel',
        value
      })
    );
  };

  const handleOnClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        component="button"
        onClick={handleOnClick}
        sx={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          bgcolor: disabled ? '#fcfcfc' : GRAY_LIGHT_TERTIARY,
          border: `1px solid ${GRAY_LIGHT_SECONDARY}`,
          padding: '0 16px',
          justifyContent: 'space-between',
          cursor: disabled ? 'auto' : 'pointer',
          transitionDuration: '150ms',
          position: 'relative',
          top: 0,
          left: 0,
          zIndex: 31,
          ...(sx || {})
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          fontFamily="signikasemibold"
          fontSize={13}
          color={disabled ? GRAY : TEXT}
          sx={{
            opacity: disabled ? 0.5 : 1
          }}
        >
          <FontAwesomeSvgIcon icon={faMagnifyingGlassPlus} fontSize="small" />
          {!minimized &&
            t(
              ZoomLevel[defaultZoomLevel ?? ZoomLevel.STANDARD].toLowerCase()
            ).toUpperCase()}
        </Box>
        {!disabled && (
          <FontAwesomeSvgIcon
            sx={{ ml: 1 }}
            icon={faAngleDown}
            fontSize="small"
          />
        )}
      </Box>

      {!disabled && (
        <Menu open={open} anchorEl={anchorEl} onClose={handleOnClose}>
          {(
            Object.values(ZoomLevel).filter(
              (v: string | ZoomLevel) =>
                Number.isInteger(v) &&
                !disableZoomLevels?.includes(v as ZoomLevel)
            ) as ZoomLevel[]
          ).map((v: ZoomLevel, i: number) => (
            <MenuItem
              key={i}
              onClick={handleOnSelect.bind(this, v)}
              sx={{
                fontFamily:
                  v == defaultZoomLevel ? 'signikabold' : 'signikaregular'
              }}
            >
              {(() => {
                switch (v as ZoomLevel) {
                  case ZoomLevel.COMPACT:
                    return t('compact');
                  case ZoomLevel.STANDARD:
                    return t('standard');
                  case ZoomLevel.DETAILED:
                    return t('detailed');
                }
              })()}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};

export default ZoomLevelMenu;
